import RefreshIcon from '@mui/icons-material/Refresh'
import Fab from '@mui/material/Fab'
import Tooltip from '@mui/material/Tooltip'
import ErrorOverlay, { AJAX_ERROR_STREAM_NOT_FOUND, AJAX_ERROR_STREAM_NOT_FOUND_SUBTITLE } from 'components/ErrorOverlay'
import FetchStream from 'components/Fetch/FetchStream'
import Loader from 'components/SimpleLoader'
import StreamView from 'components/StreamView'
import { Fragment, memo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, useParams } from 'react-router'
import { from } from 'rxjs'
import { myProfilesStreamSelector, streamsSelector, userStreamsSelector } from 'services/content/selectors'
import { refreshMyProfiles } from 'services/destinations'
import { StoreThunkDispatch } from 'store/state'
import styles from './SingleStream.pcss'

const CARD_WIDTH = 264

export const SingleStream = memo(() => {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const intl = useIntl()
  const params = useParams()
  const id = params.stream as string
  const streams = useSelector(streamsSelector)
  const userStreams = useSelector(userStreamsSelector)
  const userStream = userStreams.find(s => s.id === id)
  const isUserStream = Boolean(userStream)
  const stream = userStream || streams[id]
  const myProfilesStream = useSelector(myProfilesStreamSelector)
  const isMyProfilesStream = Boolean(id) && myProfilesStream?.id === id

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<any>(undefined)
  const [refreshingProfiles, setRefreshingProfiles] = useState(false)

  const onFeedItemShared = () => {
    return false
  }

  const onFetched = () => {
    setLoading(false)
  }

  const onFailed = (error: any) => {
    setError(error)
    setLoading(false)
  }

  const onContentItemShared = () => {
    return false
  }

  const refreshProfiles = () => {
    setRefreshingProfiles(true)
    from(dispatch(refreshMyProfiles()).unwrap())
      .subscribe(() => {
        setLoading(true)
        setRefreshingProfiles(false)
      }, () => {
        setRefreshingProfiles(false)
      })
  }

  if (!isUserStream) {
    return <Navigate key="root-redirect" replace to={`/content/streams/${id}`} />
  }

  return (
    <Fragment>
      {loading && (
        <FetchStream key="fetch" id={id} onFetched={onFetched} onFailed={onFailed}>
          {stream ? null : <Loader title="Loading Stream..." />}
        </FetchStream>
      )}
      {!stream && error && !loading && (
        <ErrorOverlay
          key="error"
          error={AJAX_ERROR_STREAM_NOT_FOUND(intl.formatMessage)}
          subtitle={AJAX_ERROR_STREAM_NOT_FOUND_SUBTITLE(intl.formatMessage)}
          originalError={error}
        />
      )}
      {stream && (
        <div
          key="stream"
          data-test="single-stream"
          className={styles.container}
        >
          <StreamView
            key="view"
            stream={stream}
            navigation={{
              prefix: `/content/my-streams/${stream.id}`,
              id: stream.id
            }}
            itemWidth={CARD_WIDTH}
            headerButton={isMyProfilesStream ? (
              <Tooltip title="Refresh my profiles" placement="top" key="btn-refresh">
                <Fab
                  disabled={refreshingProfiles}
                  color="primary"
                  aria-label="refresh"
                  className={styles['btn-refresh']}
                  onClick={refreshProfiles}
                >
                  <RefreshIcon />
                </Fab>
              </Tooltip >
            ) : undefined}
            onFeedItemShared={onFeedItemShared}
            onStreamItemShared={onContentItemShared}
          />
        </div>
      )}
      {!loading && !stream && <Navigate key="not-found" replace to="/content/my-streams" />}
    </Fragment>
  )
})

export default SingleStream
